import { addDays, addMonths, addYears, format, parseISO } from 'date-fns';
import { getKstToday } from '@/shared/lib';

/** 조회 기간 단위 — 일=단일일 / 월=단일월 / 연=단일연 / 기간=시작~종료(길이 무제한, 종료 ≤ 오늘). */
export type PeriodType = 'day' | 'month' | 'year' | 'range';

/**
 * 기간 값(단위 + 단일 기준일 + range 시작/종료) — `usePeriodSelector`(uncontrolled) 와 필터 draft(controlled)
 * 가 공유하는 상태 shape. 순수 파생 헬퍼(기본값·스텝·큐리)는 여기에 SSOT 로 둔다(중복 구현 방지).
 */
export interface PeriodValue {
  period: PeriodType;
  /** 단일 기준일 yyyy-MM-dd (day/month/year 공통 기준). */
  date: string;
  /** range 시작 yyyy-MM-dd. */
  startDate: string;
  /** range 종료 yyyy-MM-dd. */
  endDate: string;
}

/** 단위별 비교 granularity(yyyy=4·yyyy-MM=7·yyyy-MM-dd=10) — 미래 스텝 판정·쿼리 날짜 파생 공용. */
export function periodGranularity(period: PeriodType): number {
  return period === 'year' ? 4 : period === 'month' ? 7 : 10;
}

/** 단위 ±1 스텝 날짜(day=±1일·month=±1월·year=±1년). */
export function stepPeriodDate(ymd: string, unit: PeriodType, dir: -1 | 1): string {
  const d = parseISO(ymd);
  const next =
    unit === 'year' ? addYears(d, dir) : unit === 'month' ? addMonths(d, dir) : addDays(d, dir);
  return format(next, 'yyyy-MM-dd');
}

/** 기간별 최초 기본값 — 단일(day/month/year)=오늘, range=최근 N일. **N 은 기본 span 일 뿐 상한이 아니다**. */
export function periodDefaults(
  period: PeriodType,
  defaultRangeDays: number,
): Omit<PeriodValue, 'period'> {
  const today = getKstToday();
  if (period === 'range') {
    return {
      date: today,
      startDate: format(addDays(parseISO(today), -(defaultRangeDays - 1)), 'yyyy-MM-dd'),
      endDate: today,
    };
  }
  return { date: today, startDate: today, endDate: today };
}

/** 다음(미래) 스텝 가능 — 기준일이 오늘 이전(단위 granularity 비교, 오늘 초과 방지). range 는 false. */
export function canStepPeriodForward(period: PeriodType, date: string): boolean {
  if (period === 'range') return false;
  const g = periodGranularity(period);
  return date.slice(0, g) < getKstToday().slice(0, g);
}

/** 통계/기상 쿼리용 날짜 — day='yyyy-MM-dd'·month='yyyy-MM'·year='yyyy'(단위 granularity 절단). */
export function periodQueryDate(period: PeriodType, date: string): string {
  return date.slice(0, periodGranularity(period));
}
